/**
 * Thermostat OTA:
 * - T01 (and undetermined model) → always MQTT Upgrade payload "1"
 *   (device firmware uses a fixed download URL; does not accept firmware_url JSON).
 * - T02 version < 10101 → Upgrade "1" (device-fixed OtaUrl for T02@10101).
 * - T02 at/after 10101 → server firmware_url JSON (same as M24-style).
 *
 * Old T01 line has no further OTA plan beyond the fixed Upgrade "1" path.
 * UI treats them as already at 10101 so startup popup / "업데이트 필요" are not shown.
 */
export const OLD_TEMP_OTA_VERSION = 10101;
export const CONFIRMED_TEMP_MODELS = ['T01', 'T02'];

export function isTempControllerId(id) {
  return String(id || '').split('_').length < 2;
}

export function isConfirmedTempModel(model) {
  return CONFIRMED_TEMP_MODELS.includes(model);
}

/**
 * Old T01 line (explicit T01, or model not yet confirmed).
 * Always uses Upgrade "1"; T02 below 10101 still uses the legacy upgrade prompt.
 */
export function isOldT01OtaLine(controller) {
  if (!isTempControllerId(controller?._id)) return false;
  const model = String(controller?.model || '').trim().toUpperCase();
  return !model || model === 'T01';
}

/**
 * True when UI must publish Upgrade "1".
 * T01: always. T02 / undetermined: version < 10101 (or unknown).
 */
export function isLegacyTempUpgrade1(controller) {
  if (!isTempControllerId(controller?._id)) return false;
  if (isOldT01OtaLine(controller)) return true;
  if (!isConfirmedTempModel(controller?.model)) return true;
  const versionNum = Number(controller?.version);
  return Number.isNaN(versionNum) || versionNum < OLD_TEMP_OTA_VERSION;
}

/**
 * Resolve lastVersion + downloadUrl + useLegacyUpgrade1 for UI / Upgrade publish.
 * May also normalize `version` for display/compare on old T01 line.
 * @returns {{ version?: number, lastVersion: number|undefined, downloadUrl: string, useLegacyUpgrade1: boolean }}
 */
export function resolveControllerOtaMeta(controller, products = [], firmwares = []) {
  if (isLegacyTempUpgrade1(controller)) {
    // Old T01: no planned OTA — present as already at terminal version 10101.
    if (isOldT01OtaLine(controller)) {
      return {
        version: OLD_TEMP_OTA_VERSION,
        lastVersion: OLD_TEMP_OTA_VERSION,
        downloadUrl: '',
        useLegacyUpgrade1: true,
      };
    }

    return {
      lastVersion: OLD_TEMP_OTA_VERSION,
      downloadUrl: '',
      useLegacyUpgrade1: true,
    };
  }

  const targetProduct = (products || []).find((p) => p.model === controller?.model);
  const lastVersion = targetProduct?.version;
  if (lastVersion == null) {
    return { lastVersion: undefined, downloadUrl: '', useLegacyUpgrade1: false };
  }

  const firmware = (firmwares || []).find(
    (f) => f.model == controller.model && f.version == lastVersion,
  );
  return {
    lastVersion,
    downloadUrl: firmware?.downloadUrl || '',
    useLegacyUpgrade1: false,
  };
}
